import type { Permissao } from '@locaveri/shared'
import { useMemo } from 'react'

import { type AuthState, useAuthStore } from '@/stores/auth-store'

/**
 * Leitura de permissão no painel.
 *
 * ---------------------------------------------------------------------------
 * O CLIENTE NÃO AUTORIZA NADA — ELE SÓ NÃO OFERECE O QUE NÃO ADIANTA
 * ---------------------------------------------------------------------------
 * Quem autoriza é a API (guard + `@RequirePermission`). Esconder o botão no
 * painel é defesa em profundidade e, principalmente, é UX: mostrar "Criar
 * usuário" para quem vai levar 403 é prometer o que não se cumpre.
 *
 * As permissões vêm resolvidas no access token (claim `perms`) e a API as
 * devolve em `usuario.permissoes` — a UI nunca recalcula RBAC a partir de
 * papel. Consequência conhecida e documentada: revogar permissão só reflete
 * aqui no próximo access token (15 minutos), ou num F5.
 */

function listaDePermissoes(state: AuthState): readonly string[] {
  return state.usuario?.permissoes ?? []
}

/** Verificação fora do React (guard de rota do TanStack Router). */
export function temPermissao(permissao: Permissao): boolean {
  return listaDePermissoes(useAuthStore.getState()).includes(permissao)
}

/** `true` se o usuário tem PELO MENOS uma das permissões. */
export function temAlgumaPermissao(permissoes: readonly Permissao[]): boolean {
  const atuais = listaDePermissoes(useAuthStore.getState())
  return permissoes.some((permissao) => atuais.includes(permissao))
}

/**
 * Hook de permissão. Devolve booleano (e não array) de propósito: seletor que
 * devolve array novo a cada render faz o zustand re-renderizar sem parar.
 */
export function usePermissao(permissao: Permissao): boolean {
  return useAuthStore((state) => listaDePermissoes(state).includes(permissao))
}

/**
 * Conjunto de permissões, memoizado. Para quem precisa filtrar uma lista
 * inteira (o menu, por exemplo) em vez de perguntar por uma chave só.
 */
export function usePermissoes(): ReadonlySet<string> {
  const usuario = useAuthStore((state) => state.usuario)

  return useMemo(() => new Set(usuario?.permissoes ?? []), [usuario])
}
